import axios from "axios";
import { mergeDeepRight, mergeDeepLeft } from "ramda";
import { FetchError, RequestError } from "./fetchUtils";
import { notBlank } from "./general";

// axios error handling: https://axios-http.com/docs/handling_errors
const checkError = async (error, props) => {
  if (error.response) {
    const { response } = error;
    if (response.status === 404) {
      throw new FetchError(response.status, response.statusText, {
        message: `Not Found [${response.url}]`,
        errors: [
          {
            message: response.statusMessage,
            code: "error.not_found",
          },
        ],
      });
    } else if (response.status === 401 || response.status === 403) {
      throw new FetchError(response.status, response.statusText, {
        message: "Unauthorized",
        errors: [
          {
            message: response.statusMessage,
            code: "error.unauthorized",
          },
        ],
      });
    } else {
      const errorBody = response.data;
      if (errorBody && errorBody.message) {
        throw new FetchError(response.status, response.statusText, errorBody);
      } else if (errorBody && errorBody.error) {
        throw new FetchError(response.status, response.statusText, {
          message: errorBody.error,
          errors: [
            {
              message: errorBody.error,
              code: `error.bad_${props.method.toLowerCase()}`,
            },
          ],
        });
      } else {
        throw new FetchError(response.status, response.statusText, {
          message: `Unable to do ${props.method.toLowerCase()}.`,
          errors: [
            {
              message: response.statusMessage,
              code: `error.bad_${props.method.toLowerCase()}`,
            },
          ],
        });
      }
    }
  } else if (error.code === "ERR_CANCELED") {
    throw new RequestError(error.code, error.message, {
      message: `Unable to do ${props.method.toLowerCase()}`,
      errors: [
        { message: "The request was canceled", code: `error.canceled_request` },
      ],
    });
  } else if (error.request) {
    const { request } = error;
    throw new FetchError(request.status, "Request Error", {
      message: `Unable to do ${props.method.toLowerCase()}`,
      errors: [
        {
          message: "An error occurred while performing a request.",
          code: `error.bad_${props.method.toLowerCase()}`,
        },
      ],
    });
  } else {
    throw new FetchError(400, "Setup Error", {
      message: `Unable to do ${props.method.toLowerCase()}`,
      errors: [{ message: error.message, code: `error.setup_error` }],
    });
  }
};

export const doFetch = async (url, props) => {
  try {
    const config = mergeDeepRight({ crossDomain: true }, props);
    const response = await axios({
      url,
      ...config,
    });
    if (notBlank(response.data)) {
      return response.data;
    } else {
      return null;
    }
  } catch (err) {
    return checkError(err, props);
  }
};

export const doPost = async (url, data, props = {}) => {
  return doFetch(
    url,
    mergeDeepLeft(props, {
      method: "POST",
      data: JSON.stringify(data),
      headers: {
        "Content-Type": "application/json",
      },
    })
  );
};

export const doPut = async (url, body = {}, props = {}) => {
  const origin = document.location.origin;
  return doFetch(
    url,
    mergeDeepLeft(props, {
      method: "PUT",
      data: body,
      headers: {
        Origin: origin,
      },
    })
  );
};
